<template>
  <div
    class="accounts-header">
    <div :class="['accounts-header__container', { '_without-picture': !product.picture_item }]">
      <breadcrumbs
        :items="breadcrumbs"
        class="accounts-header__breadcrumbs"
        @tagId="setActiveTag" />

      <div
        class="accounts-header__title">
        <h1>
          {{ headerInfo.title }}
          <span
            v-if="headerInfo.subtitle"
            class="accounts-header__subtitle">
            {{ headerInfo.subtitle }}
          </span>
        </h1>
      </div>

      <p
        v-if="headerInfo.description"
        class="accounts-header__description"
        v-html="headerInfo.description" />

      <offer-advantages
        v-if="headerInfo.advantages"
        list-model-key="LIST_ACCOUNTS"
        :is-icon-compact="headerInfo.advantages.isIconCompact"
        theme="alt"
        class="accounts-header__features" />

      <div class="accounts-header__desktop-wrapper">
        <div
          v-if="product.picture_item"
          class="accounts-header__image">
          <responsive-image
            :image-data="{
              default: productDetailImage.small || productDetailImage.large,
              fallback: '/images/games/picture_item.png',
              sources: [
                {
                  media: '(-webkit-min-device-pixel-ratio: 2)',
                  srcset: productDetailImage.large,
                },
              ],
            }" />
        </div>

        <div
          v-if="headerInfo.rating"
          class="accounts-header__rating">
          <ui-star-rating
            :rating="+headerInfo.rating.rate"
            :orders-count="+headerInfo.rating.count" />
        </div>
      </div>

      <div class="accounts-header__filters">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import Breadcrumbs from 'Components/ui/Breadcrumbs/Breadcrumbs.vue';
import UiStarRating from 'Components/ui/UiStarRating.vue';
import ResponsiveImage from 'Components/ui/Image/ResponsiveImage.vue';
import OfferAdvantages from 'Root/pages/game/components/OfferAdvantages.vue';

export default {
  name: 'GameAccountsHeader',

  components: {
    UiStarRating,
    Breadcrumbs,
    ResponsiveImage,
    OfferAdvantages,
  },

  computed: {
    ...mapState('products', ['headerInfo', 'product']),
    ...mapGetters('products', ['breadcrumbs']),

    productDetailImage() {
      return {
        small: this.product?.picture_item_small ? `/storage${this.product.picture_item_small}` : null,
        large: this.product?.picture_item ? `/storage${this.product.picture_item}` : null,
      };
    },
  },

  methods: {
    ...mapMutations('products', ['mutateActiveTag']),

    setActiveTag(id) {
      this.mutateActiveTag(id);
    },
  },
};
</script>

<style lang="scss" scoped>
.accounts-header {
  position: relative;
  z-index: 2;
  padding-top: 16px;
  line-height: 1.2;
}

.accounts-header__container {
  display: grid;
  grid-template-columns: minmax(1px, 108fr) minmax(1px, 235fr);
  grid-template-areas: 'breadcrumbs breadcrumbs'
                        'title image'
                        'rating rating'
                        'description description'
                        'features features'
                        'filters filters';
  align-items: start;
}

.accounts-header__container._without-picture {
  grid-template-columns: minmax(1px, 1fr);
  grid-template-areas: 'breadcrumbs'
                        'title'
                        'rating '
                        'description'
                        'features'
                        'filters';

  .accounts-header__rating {
    margin: 0 0 24px 0;
  }
}

.accounts-header__breadcrumbs {
  grid-area: breadcrumbs;
  margin-bottom: 24px;
}

.accounts-header__title {
  grid-area: title;
  position: relative;
  z-index: 2;
  min-width: 182px;
  margin-bottom: 24px;
  font-weight: 500;
  font-size: 21px;
  line-height: normal;

  span {
    color: $color--basic-light-gray;
    margin-left: 8px;
  }
}

.accounts-header__description {
  grid-area: description;
  min-height: 56px;
  font-weight: 400;
  font-size: 13px;
}

.accounts-header__features {
  grid-area: features;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 0;
}

.accounts-header__filters {
  grid-area: filters;
}

.accounts-header__image {
  grid-area: image;
  display: flex;
  width: 100%;
  min-height: 176px;
  transform: scale(1.2);

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
}

.accounts-header__rating {
  position: relative;
  z-index: 2;
  grid-area: rating;
  width: 100%;
  max-width: 220px;
  margin-top: -30px;
}

@media screen and #{$screen-more-or-640} {
  .accounts-header {
    padding-top: 24px;
  }

  .accounts-header__container {
    display: grid;
    grid-template-columns: minmax(1px, 1fr) 229px;
    grid-template-rows: auto auto 1fr;
    grid-template-areas: 'breadcrumbs image'
                        'title image'
                        'description image'
                        'description rating'
                        'features features'
                        'filters filters';
    column-gap: 4px;
  }

  .accounts-header__container._without-picture {
    grid-template-columns: minmax(1px, 1fr) 229px;
    grid-template-areas: 'breadcrumbs rating'
                        'title rating'
                        'description description'
                        'features features'
                        'filters filters';
    column-gap: 16px;

    .accounts-header__rating {
      justify-self: flex-end;
      align-self: flex-start;
      margin: 0;
    }
  }

  .accounts-header__breadcrumbs {
    margin-bottom: 16px;
  }

  .accounts-header__title {
    font-size: 35px;
  }

  .accounts-header__description {
    font-size: 15px;
  }

  .accounts-header__image {
    transform: scale(1);
  }

  .accounts-header__desktop-wrapper {
    grid-area: image;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
}

@media screen and #{$screen-more-or-1024} {
  .accounts-header {
    padding-top: 40px;
  }

  .accounts-header__container {
    grid-template-columns: minmax(1px, 644fr) minmax(1px, 468fr);
    grid-template-rows: auto auto auto auto 1fr;
    grid-template-areas: 'breadcrumbs image'
                        'title image'
                        'description image'
                        'features image'
                        'filters image';
    align-items: start;
  }

  .accounts-header__rating {
    grid-area: image;
  }

  .accounts-header__container._without-picture {
    grid-template-areas: 'breadcrumbs rating'
                        'title rating'
                        'description rating'
                        'features rating'
                        'filters rating';

    .accounts-header__rating {
      grid-area: rating;
    }
  }

  .accounts-header__features {
    flex-wrap: wrap;
    padding-right: 15px;
  }

  .accounts-header__title,
  .accounts-header__description,
  .accounts-header__features,
  .accounts-header__filters {
    max-width: 850px;
  }
}

@media screen and #{$screen-more-or-1366} {
  .accounts-header__container {
    grid-template-columns: minmax(1px, 1fr) 468px;
    grid-template-areas: 'breadcrumbs image'
                        'title image'
                        'description image'
                        'features image'
                        'filters image';
  }

  .accounts-header__container._without-picture {
    grid-template-areas: 'breadcrumbs rating'
                        'title rating'
                        'description rating'
                        'features rating'
                        'filters rating';
  }
}

@media screen and (min-width: 1660px) {
  .accounts-header__container {
    grid-template-columns: minmax(850px, 1fr) minmax(468px, 1fr);
  }

  .accounts-header__image {
    max-height: 360px;
    mask-image: linear-gradient(to top, transparent, black 40%);
  }
}

@media screen and #{$screen-less-than-1024} {
  .accounts-header__features {
    width: calc(100% + var(--padding) * 2);
    margin-left: calc(-1 * var(--padding));
    padding: 24px var(--padding) 24px var(--padding);
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    gap: 24px;

    &::-webkit-scrollbar {
      display: none;
    }

    > * {
      flex-shrink: 0;
    }
  }
}

@media screen and #{$screen-less-than-640} {
  .accounts-header__features {
    padding: 24px var(--padding) 0 var(--padding);
  }

  .accounts-header__desktop-wrapper {
    display: contents;
  }

  .accounts-header__rating {
    margin-bottom: 24px;
  }
}
</style>
